import moment from 'moment-timezone'
import React, { useMemo } from 'react'
import { Field, Form, Formik } from 'formik'
import { Close as CloseIcon } from '@material-ui/icons'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@material-ui/core'

import yup from 'civic-champs-shared/utils/yup'
import { DraggablePaper, OutlinedButton, ContainedButton } from 'civic-champs-shared'
import { StyledKeyboardTimePicker } from 'civic-champs-shared/formik/components/StyledFormikDateTimePickers'
import StyledOutlinedTextField from 'civic-champs-shared/formik/components/StyledOutlinedTextFieldComponent'
import { useTimeZone } from 'civic-champs-shared/auth/hooks'
import { EditableCheckOut } from '../types'
import { CheckOutRoleField } from './CheckOutRoleField'
import { useStyles } from 'components/FormModal'
import { testEndIsAfterStart } from 'Event/validation/eventDatePayloadSchema'
import { useDateTimeUtils } from 'civic-champs-shared/utils/useDateTimeUtils'

const editableCheckOutSchema = yup.object({
  checkedInAt: yup
    .date()
    .required('A check-in time is required. Please enter a check-in time.')
    .typeError('A check-in time is required. Please enter a check-in time.'),
  checkedOutAt: yup
    .date()
    .test('min-by-time', 'The check-out cannot occur before the check-in.', function (value: any) {
      const start = this.parent.checkedInAt
      return testEndIsAfterStart(start, start, start, value)
    })
    .required('A check-out time is required. Please enter a check-out time.')
    .typeError('A check-out time is required. Please enter a check-out time.'),
  role: yup.object().nullable().notRequired(),
})

interface CancelEditCheckOut {
  proceed: false
}

interface CompleteEditCheckOut {
  proceed: true
  checkOut: EditableCheckOut
}

export type EditCheckOutResult = CancelEditCheckOut | CompleteEditCheckOut

export interface EditCheckOutPromptProps {
  showing: boolean
  complete: (result: EditCheckOutResult) => Promise<void>
  checkOut: EditableCheckOut
}

export const EditCheckOutPrompt = (props: EditCheckOutPromptProps) => {
  const { showing, complete, checkOut } = props
  const classes = useStyles()

  const tz = useTimeZone()
  const date = moment(checkOut.checkedInAt)

  const { fakeAsLocalTimeForUI, unfakeAsLocalTimeForUI, compileDateTime } = useDateTimeUtils()
  const initialValues = useMemo(
    () => ({
      ...checkOut,
      checkedInAt: fakeAsLocalTimeForUI(checkOut.checkedInAt).toDate(),
      checkedOutAt: fakeAsLocalTimeForUI(checkOut.checkedOutAt).toDate(),
    }),
    [checkOut, fakeAsLocalTimeForUI],
  )

  const onCancel = () => {
    complete({ proceed: false })
  }

  const onSubmit = (values: EditableCheckOut) => {
    complete({
      proceed: true,
      checkOut: {
        ...checkOut,
        checkedInAt: compileDateTime(date, unfakeAsLocalTimeForUI(values.checkedInAt)).toDate(),
        checkedOutAt: compileDateTime(date, unfakeAsLocalTimeForUI(values.checkedOutAt)).toDate(),
        role: values.role,
      },
    })
  }

  return (
    <Dialog
      open={showing}
      PaperComponent={DraggablePaper}
      // @ts-ignore
      PaperProps={{ handle: `#edit-checkout-modal` }}
      aria-labelledby="draggable-dialog-title"
      maxWidth="xs"
      classes={{ paper: classes.dialog }}
      disableEnforceFocus
    >
      <Formik initialValues={initialValues} validationSchema={editableCheckOutSchema} onSubmit={onSubmit}>
        {({ isSubmitting, submitForm }) => (
          <Form>
            <DialogTitle classes={{ root: classes.title }} disableTypography={true} id="edit-checkout-modal">
              Line Edit Check-Out
              <IconButton className={classes.dialogCloseButton} onClick={onCancel}>
                <CloseIcon className={classes.dialogCloseIcon} />
              </IconButton>
            </DialogTitle>
            <DialogContent classes={{ root: classes.content }}>
              <StyledOutlinedTextField
                moreHeight={true}
                label="Opportunity"
                value={`${checkOut.shift.name} (${date.tz(tz).format('M/D/YYYY')})`}
                disabled={true}
              />
              <StyledOutlinedTextField
                moreHeight={true}
                label="Volunteer"
                value={`${checkOut.user.givenName} ${checkOut.user.familyName}`}
                disabled={true}
              />
              <Field
                higher
                inputVariant="outlined"
                label="Check-In"
                component={StyledKeyboardTimePicker}
                name="checkedInAt"
              />
              <Field
                higher
                inputVariant="outlined"
                label="Check-Out"
                component={StyledKeyboardTimePicker}
                name="checkedOutAt"
              />
              <Field
                component={CheckOutRoleField}
                roles={checkOut.shift.roles}
                label="Role"
                placeholder="Select a Role"
                name="role"
              />
            </DialogContent>
            <DialogActions className={classes.actions}>
              <OutlinedButton className={classes.back} disabled={isSubmitting} onClick={onCancel}>
                Cancel
              </OutlinedButton>
              <ContainedButton className={classes.next} disabled={isSubmitting} onClick={submitForm}>
                Update Check-Out
              </ContainedButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
